import { capture } from '@parcel/analytics/browser';
import { Lock } from 'lucide-react';
import { useCaptureOnce } from '../../lib/analytics.js';
import { useEntitlements } from '../../lib/entitlements.js';
import { UpgradeButton } from './upgrade-button.js';

export interface Withheld {
  count: number;
  oldestFiledAt: string | null;
}

export function shouldShowSignalGate(withheld: Withheld | undefined): boolean {
  return Boolean(withheld) && (withheld?.count ?? 0) > 0;
}

/** Count-bearing line (pluralized). Pure, so it is unit-tested without a render. */
export function signalGateCopy(count: number): string {
  return `${count} earlier ${count === 1 ? 'signal' : 'signals'}`;
}

function GhostRows({ rows }: { rows: number }) {
  // Blurred placeholders shaped like real signal rows, dimmed behind the upgrade card so the
  // gate reads as "there is locked content here". No real data is rendered: older signals are
  // never sent to a capped (Free) workspace.
  return (
    <div aria-hidden className="select-none opacity-50 blur-[2.5px]">
      {Array.from({ length: rows }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static decorative placeholders
        <div key={i} className="flex items-center gap-3 border-t px-5 py-3.5">
          <div className="size-8 shrink-0 rounded-md bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/5 rounded bg-muted" />
            <div className="h-2.5 w-1/4 rounded bg-muted" />
          </div>
          <div className="h-2.5 w-8 shrink-0 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

export function SignalGateOverlay({
  withheld,
  workspaceSlug,
}: {
  /** Retained for call-site context (home feed `card` vs drawer `pill`); rendering is shared. */
  variant: 'card' | 'pill';
  withheld: Withheld | undefined;
  workspaceSlug: string;
}) {
  const { effectivePlan, isLoading } = useEntitlements(workspaceSlug);
  const visible = shouldShowSignalGate(withheld) && Boolean(withheld);

  // Keyed on visibility and on entitlements having resolved, so it fires when
  // the gate actually appears, with the real plan, and not again on re-renders.
  useCaptureOnce(visible && !isLoading ? 'signal_history' : null, () =>
    capture('paywall_viewed', {
      gate: 'signal_history',
      required_plan: 'starter',
      current_plan: effectivePlan,
    }),
  );

  if (!visible || !withheld) return null;

  // Blurred ghost rows with a solid upgrade card floating on top: the rows read as locked
  // content, the card carries the count + CTA. Same treatment on the home feed and the drawers.
  return (
    <div className="relative border-t">
      <GhostRows rows={2} />
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg">
          <Lock className="h-4 w-4 shrink-0 text-brand" aria-hidden />
          <div className="text-left">
            <div className="text-sm font-medium text-foreground">
              {signalGateCopy(withheld.count)}
            </div>
            <div className="text-xs text-muted-foreground">
              Full signal history is a Starter feature.
            </div>
          </div>
          <UpgradeButton workspaceSlug={workspaceSlug} gate="signal_history" size="sm" />
        </div>
      </div>
    </div>
  );
}
